import { useState, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { supabase, type FacebookGroup } from "@/lib/supabaseClient";

const TOPIC_LIST = [
  "Junk Journaling",
  "Snail Mail",
  "Ephemera Swaps",
  "Washi Tape",
  "Stamping & Ink",
  "Mixed Media",
  "Paper Crafting",
  "Bookbinding",
  "Penpal Exchange",
  "Art Journaling",
];

const GROUP_TYPES = ["Public", "Private", "Buy/Sell/Trade", "Swap Group", "Educational", "Inspiration"];

const MEMBER_RANGES = [
  "Under 500",
  "500–2,000",
  "2,000–10,000",
  "10,000–50,000",
  "50,000+",
];

export default function GroupsPage() {
  const [groups, setGroups] = useState<FacebookGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedTopic, setSelectedTopic] = useState("");
  const [selectedType, setSelectedType] = useState("");

  useEffect(() => {
    document.title = "Facebook Groups Directory — Junk Journal & Snail Mail Communities | Junk & Post";
    const fetchGroups = async () => {
      const { data, error } = await supabase
        .from("facebook_groups")
        .select("*")
        .eq("approved", true)
        .order("featured", { ascending: false })
        .order("created_at", { ascending: false });
      if (!error && data) setGroups(data as FacebookGroup[]);
      setLoading(false);
    };
    fetchGroups();
  }, []);

  const filtered = useMemo(() => {
    let list = groups;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (g) =>
          g.group_name.toLowerCase().includes(q) ||
          (g.description || "").toLowerCase().includes(q) ||
          (g.topics || "").toLowerCase().includes(q) ||
          g.admin_name.toLowerCase().includes(q)
      );
    }
    if (selectedTopic) {
      list = list.filter((g) => (g.topics || "").includes(selectedTopic));
    }
    if (selectedType) {
      list = list.filter((g) => g.group_type === selectedType);
    }
    return list;
  }, [groups, search, selectedTopic, selectedType]);

  const featured = filtered.filter((g) => g.featured);
  const regular = filtered.filter((g) => !g.featured);

  return (
    <div className="min-h-screen bg-[#faf8f4]">
      <Navbar />

      {/* Header */}
      <div className="pt-24 pb-12 bg-[#3a2410]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-[#f0c87a] text-xs font-semibold uppercase tracking-widest mb-4">
            <i className="ri-group-line"></i>
            Community Groups
          </div>
          <h1
            className="text-4xl font-bold text-white mb-3"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Facebook Groups Directory
          </h1>
          <p className="text-[#c9a87c] max-w-xl mx-auto mb-6">
            Discover junk journal, snail mail, and paper crafting communities on Facebook.
            Find your people and join the conversation.
          </p>
          <Link
            to="/groups/submit"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#f0c87a] text-[#2c1e0f] font-semibold text-sm hover:bg-[#e8bc62] transition-colors whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            List Your Group
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="border-b border-[#e8e0d4] bg-white sticky top-16 z-40">
        <div className="max-w-6xl mx-auto px-6 py-4 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
          <div className="relative flex-1 min-w-0">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-[#9a8a78]">
              <i className="ri-search-line text-sm"></i>
            </div>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search groups by name, topic, or admin..."
              className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-[#ede5d8] text-sm text-[#3a2e1e] placeholder-[#9a8a78] focus:outline-none focus:border-[#c9a87c] bg-[#faf8f4]"
            />
          </div>
          <select
            value={selectedTopic}
            onChange={(e) => setSelectedTopic(e.target.value)}
            className="px-3 py-2.5 rounded-xl border border-[#ede5d8] text-sm text-[#3a2e1e] bg-[#faf8f4] focus:outline-none focus:border-[#c9a87c] cursor-pointer whitespace-nowrap"
          >
            <option value="">All Topics</option>
            {TOPIC_LIST.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="px-3 py-2.5 rounded-xl border border-[#ede5d8] text-sm text-[#3a2e1e] bg-[#faf8f4] focus:outline-none focus:border-[#c9a87c] cursor-pointer whitespace-nowrap"
          >
            <option value="">All Types</option>
            {GROUP_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-10">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-white rounded-2xl border border-[#ede5d8] h-56 animate-pulse" />
            ))}
          </div>
        ) : groups.length === 0 ? (
          <div className="text-center py-24">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-5 rounded-full bg-[#f5ede0] text-[#c9a87c]">
              <i className="ri-group-line text-3xl"></i>
            </div>
            <h2 className="text-2xl font-bold text-[#2c1e0f] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
              No Groups Listed Yet
            </h2>
            <p className="text-[#7a6555] mb-6 max-w-sm mx-auto">
              Be the first to list your Facebook group and connect with the community!
            </p>
            <Link
              to="/groups/submit"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#8b5e3c] text-white font-semibold text-sm hover:bg-[#7a5234] transition-colors whitespace-nowrap"
            >
              <i className="ri-add-line"></i>List Your Group
            </Link>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 text-[#c9a87c]">
              <i className="ri-search-line text-3xl"></i>
            </div>
            <p className="text-[#7a6555]">No groups match your search. Try different keywords or filters.</p>
            <button
              onClick={() => { setSearch(""); setSelectedTopic(""); setSelectedType(""); }}
              className="mt-4 text-sm text-[#8b5e3c] underline cursor-pointer"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <>
            {/* Featured Groups */}
            {featured.length > 0 && (
              <div className="mb-10">
                <div className="flex items-center gap-2 mb-5">
                  <div className="w-5 h-5 flex items-center justify-center text-[#f0c87a]">
                    <i className="ri-star-fill text-sm"></i>
                  </div>
                  <h2 className="text-sm font-bold text-[#3a2e1e] uppercase tracking-widest">Featured Groups</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {featured.map((group) => (
                    <GroupCard key={group.id} group={group} />
                  ))}
                </div>
              </div>
            )}

            {/* All Groups */}
            {regular.length > 0 && (
              <div>
                {featured.length > 0 && (
                  <h2 className="text-sm font-bold text-[#3a2e1e] uppercase tracking-widest mb-5">All Groups</h2>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {regular.map((group) => (
                    <GroupCard key={group.id} group={group} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <Footer />
    </div>
  );
}

function SharePopover({
  group,
  onClose,
}: {
  group: FacebookGroup;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);

  const shareUrl = group.facebook_url;
  const shareText = `Check out "${group.group_name}" — a junk journaling & paper crafting community on Facebook!`;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
    }
  };

  const fbShare = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
  const pinterestShare = `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(shareUrl)}&description=${encodeURIComponent(shareText)}`;

  return (
    <div
      ref={ref}
      className="absolute bottom-full right-0 mb-2 w-56 bg-white rounded-xl border border-[#ede5d8] shadow-lg z-50 overflow-hidden"
    >
      <div className="px-3 py-2 border-b border-[#f0e8d8]">
        <p className="text-xs font-semibold text-[#3a2e1e] uppercase tracking-widest">Share this group</p>
      </div>
      <div className="p-1.5 flex flex-col gap-0.5">
        <button
          onClick={handleCopy}
          className="flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-[#faf8f4] text-sm text-[#3a2e1e] transition-colors cursor-pointer w-full text-left whitespace-nowrap"
        >
          <div className="w-7 h-7 flex items-center justify-center rounded-full bg-[#f5ede0] text-[#8b5e3c]">
            <i className={copied ? "ri-check-line text-sm" : "ri-link text-sm"}></i>
          </div>
          {copied ? "Link copied!" : "Copy link"}
        </button>
        <a
          href={fbShare}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-[#faf8f4] text-sm text-[#3a2e1e] transition-colors cursor-pointer whitespace-nowrap"
        >
          <div className="w-7 h-7 flex items-center justify-center rounded-full bg-[#1877F2]/10 text-[#1877F2]">
            <i className="ri-facebook-fill text-sm"></i>
          </div>
          Share on Facebook
        </a>
        <a
          href={pinterestShare}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-[#faf8f4] text-sm text-[#3a2e1e] transition-colors cursor-pointer whitespace-nowrap"
        >
          <div className="w-7 h-7 flex items-center justify-center rounded-full bg-rose-50 text-rose-600">
            <i className="ri-pinterest-fill text-sm"></i>
          </div>
          Share on Pinterest
        </a>
      </div>
    </div>
  );
}

function GroupCard({ group }: { group: FacebookGroup }) {
  const [showShare, setShowShare] = useState(false);
  const topics = (group.topics || "").split(",").map((t) => t.trim()).filter(Boolean);

  const typeColors: Record<string, string> = {
    "Public": "bg-green-50 text-green-700",
    "Private": "bg-amber-50 text-amber-700",
    "Buy/Sell/Trade": "bg-rose-50 text-rose-700",
    "Swap Group": "bg-[#f5ede0] text-[#8b5e3c]",
    "Educational": "bg-sky-50 text-sky-700",
    "Inspiration": "bg-violet-50 text-violet-700",
  };

  return (
    <div className="bg-white rounded-2xl border border-[#ede5d8] overflow-hidden flex flex-col hover:border-[#c9a87c] transition-colors group">
      {/* Image / Banner */}
      <div className="relative h-36 bg-[#f5ede0] overflow-hidden">
        {group.image_url ? (
          <img
            src={group.image_url}
            alt={group.group_name}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#ede5d8] text-[#c9a87c]">
              <i className="ri-facebook-circle-fill text-4xl"></i>
            </div>
          </div>
        )}
        {group.featured && (
          <div className="absolute top-3 left-3 flex items-center gap-1 px-2 py-1 rounded-full bg-[#f0c87a] text-[#2c1e0f] text-xs font-bold">
            <i className="ri-star-fill text-xs"></i> Featured
          </div>
        )}
        {group.group_type && (
          <div className={`absolute top-3 right-3 px-2 py-0.5 rounded-full text-xs font-semibold ${typeColors[group.group_type] || "bg-[#f5ede0] text-[#8b5e3c]"}`}>
            {group.group_type}
          </div>
        )}
      </div>

      {/* Body */}
      <div className="p-4 flex flex-col flex-1">
        <h3
          className="font-bold text-[#2c1e0f] text-base mb-0.5 line-clamp-1"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          {group.group_name}
        </h3>
        <p className="text-xs text-[#9a8a78] mb-1 flex items-center gap-1">
          <i className="ri-user-line text-xs"></i> Admin: {group.admin_name}
          {group.location && (
            <span className="ml-2 flex items-center gap-0.5">
              <i className="ri-map-pin-line text-xs"></i>{group.location}
            </span>
          )}
        </p>

        {group.member_count && (
          <p className="text-xs text-[#8b5e3c] font-semibold mb-2 flex items-center gap-1">
            <i className="ri-group-line text-xs"></i> {group.member_count} members
          </p>
        )}

        {group.description && (
          <p className="text-xs text-[#6b5a4a] leading-relaxed mb-3 line-clamp-3 flex-1">
            {group.description}
          </p>
        )}

        {topics.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-4">
            {topics.slice(0, 3).map((t) => (
              <span key={t} className="px-2 py-0.5 rounded-full bg-[#f5ede0] text-[#8b5e3c] text-xs">
                {t}
              </span>
            ))}
            {topics.length > 3 && (
              <span className="px-2 py-0.5 rounded-full bg-[#f5ede0] text-[#9a8a78] text-xs">
                +{topics.length - 3} more
              </span>
            )}
          </div>
        )}

        <div className="flex gap-2 mt-auto">
          <a
            href={group.facebook_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#1877F2]/10 text-[#1877F2] text-sm font-semibold hover:bg-[#1877F2]/20 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-facebook-circle-fill text-base"></i>
            Join on Facebook
          </a>
          <div className="relative">
            <button
              onClick={() => setShowShare((v) => !v)}
              className="flex items-center justify-center w-10 h-10 rounded-xl border border-[#ede5d8] bg-[#faf8f4] text-[#8b5e3c] hover:bg-[#f5ede0] hover:border-[#c9a87c] transition-colors cursor-pointer"
              title="Share this group"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-share-line text-base"></i>
              </div>
            </button>
            {showShare && (
              <SharePopover group={group} onClose={() => setShowShare(false)} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
